// src/data/unleashedPushQueue.ts
// ------------------------------
// Repository for `unleashed_push_queue` (db-133) — the per-change push queue for
// the DFV Services -> Unleashed AUTO-sync (model B). Every DFV Service change point
// enqueues ONE cheap row here; a cron-driven worker (built in a later batch) drains
// it. Typed via getTypedClient().
//
// This batch ships the ENQUEUE side only (the gate + dedupe + insert). The worker,
// on/off flag, queue-management view and alerting land in later batches. Enqueuing
// rows before the worker exists is inert — nothing drains them yet (same way db-63
// landed the SO-push queue ahead of its worker).

import { isZephyrCode } from "../../supabase/functions/_shared/unleashed/autoSync";
import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";
import { getMembershipForContactDelete, listUserSheltersByUserId } from "./userShelters";

export type QueueRow = Database["public"]["Tables"]["unleashed_push_queue"]["Row"];
type QueueInsert = Database["public"]["Tables"]["unleashed_push_queue"]["Insert"];
type WriteResult = { error: { message: string } | null; skipped?: boolean };

export type UnleashedEntity = "customer" | "contact";
export type UnleashedOp = "upsert" | "delete";

export interface EnqueueArgs {
  entityType: UnleashedEntity;
  entityId: string | null | undefined; // shelters.id (customer) | user_shelters.id (contact)
  op?: UnleashedOp; // default "upsert"
  shelterCode: string | null | undefined; // for the Zephyr-only gate
  // Optional per-job data (db-135). Used ONLY by a DETACH contact delete to carry the
  // GUIDs { customer_guid, contact_guid }, because the user_shelters row is hard-deleted
  // before the worker runs so the GUIDs can't be read back. Omit for everything else.
  payload?: Record<string, unknown> | null;
}

// Enqueue a DFV Service change for the Unleashed auto-sync worker.
//
// - ZEPHYR-ONLY GATE: a non-Zephyr shelter_code is silently skipped (returns
//   { error:null, skipped:true }) — TEST…/non-Zephyr services are never auto-pushed
//   (John, 3 Aug). The manual Sync Utilities don't call this.
// - DEDUPE: if an active (queued|hold) job already exists for the same
//   (entity_type, entity_id, op), no new row is added — the worker reads the
//   record's CURRENT state at drain time (read-modify-write), so one pending job
//   covers all pending edits.
// - NEVER THROWS / never blocks the caller's save: on any client/DB error it logs
//   and returns { error } (or { error:null } when there's no client). Callers may
//   `await` it or fire-and-forget with `void`.
//
// NOTE: rows are enqueued with the DB default status 'queued'. The on/off flag
// (enqueue + HOLD while the sync is off) is added in a later batch; until then the
// (not-yet-built) worker is inert, so a 'queued' row simply waits.
export async function enqueueUnleashedPush(args: EnqueueArgs): Promise<WriteResult> {
  const op: UnleashedOp = args.op ?? "upsert";

  // Gate: only auto-push Zephyr* services.
  if (!isZephyrCode(args.shelterCode)) return { error: null, skipped: true };
  // Nothing to enqueue without a record id.
  if (!args.entityId) return { error: null, skipped: true };
  const entityId = args.entityId;

  const c = getTypedClient();
  if (!c) return { error: null };

  // Dedupe against an already-pending job for the same record + op.
  const { data: existing, error: exErr } = await c
    .from("unleashed_push_queue")
    .select("id")
    .eq("entity_type", args.entityType)
    .eq("entity_id", entityId)
    .eq("op", op)
    .in("status", ["queued", "hold"])
    .limit(1);
  if (exErr) {
    // Non-fatal: fall through and insert anyway — a redundant job is harmless
    // (the worker's upsert is idempotent).
    logger.warn("enqueueUnleashedPush dedupe check failed:", exErr.message);
  } else if (existing && existing.length > 0) {
    return { error: null, skipped: true };
  }

  const row: Record<string, unknown> = {
    entity_type: args.entityType,
    entity_id: entityId,
    op,
  };
  // Carry the detach GUID snapshot when provided (db-135); NULL/omitted otherwise.
  if (args.payload != null) row.payload = args.payload;
  const { error } = await c.from("unleashed_push_queue").insert(row as QueueInsert);
  if (error) logger.warn("enqueueUnleashedPush insert failed:", error.message);
  return { error };
}

// Enqueue a contact push for EVERY membership of a LOGIN user — used after a change
// that affects the person across their services (create / attach / edit / primary /
// reactivate → "upsert"; deactivate → "delete"). The membership write helpers don't
// return the row id, so we RE-LIST to get the current ids; each row is Zephyr-gated
// inside enqueueUnleashedPush. On a "delete" the membership row still exists (deactivate
// flags the profile, not the membership), so the worker reads the GUIDs from the row —
// no carried payload needed (that's only for DETACH, where the row is hard-deleted).
// Fire-and-forget; never throws / never blocks the caller. No-Login contacts (user_id
// NULL) aren't returned here — they enqueue by membership id at their own change points.
export async function enqueueContactPushForUser(
  userId: string | null | undefined,
  op: UnleashedOp = "upsert",
  // Optional display-only diff (db-135 payload.changes) stamped on each job so the Push
  // List inspect modal can show WHAT changed. The person-level edit is the same across
  // all their memberships, so the same changes ride every row. Deduped jobs keep the
  // FIRST payload; the worker still read-modify-writes the current state, so a stale
  // display never affects the actual push.
  payload?: Record<string, unknown> | null
): Promise<void> {
  if (!userId) return;
  try {
    const rows = (await listUserSheltersByUserId(userId)) || [];
    for (const m of rows) {
      if (!m.id) continue;
      await enqueueUnleashedPush({
        entityType: "contact",
        entityId: m.id,
        op,
        shelterCode: m.shelter_code,
        payload,
      });
    }
  } catch (e) {
    logger.warn("enqueueContactPushForUser failed:", e);
  }
}

// Enqueue a contact DELETE for a membership. Must be AWAITED before the contact is
// deactivated (db-189, which NULLs the GUID) or a login user is detached (hard delete):
// it reads the membership's GUIDs while they still exist and carries them on the job
// (db-135 payload), because the worker can't read them back afterwards. Skips when the
// contact was never linked (no GUID —
// nothing to delete in Unleashed) or the code isn't Zephyr (enqueue gates that too). Never
// throws. (Deactivate uses the delete path in enqueueContactPushForUser instead — there
// the row survives, so no snapshot is needed.)
export async function enqueueContactDeleteForMembership(
  membershipId: string | null | undefined
): Promise<void> {
  if (!membershipId) return;
  try {
    const m = await getMembershipForContactDelete(membershipId);
    if (!m?.contact_guid) return; // never linked → nothing to delete
    await enqueueUnleashedPush({
      entityType: "contact",
      entityId: membershipId,
      op: "delete",
      shelterCode: m.shelter_code,
      payload: { customer_guid: m.customer_guid, contact_guid: m.contact_guid },
    });
  } catch (e) {
    logger.warn("enqueueContactDeleteForMembership failed:", e);
  }
}

// Enqueue a contact push for ONE membership by id. Used by the No-Login contact save,
// which addresses login-less rows by membership id (they have user_id NULL, so
// listUserSheltersByUserId doesn't return them). Reads the membership's shelter_code for
// the Zephyr gate. Fire-and-forget; never throws.
export async function enqueueContactPushForMembership(
  membershipId: string | null | undefined,
  op: UnleashedOp = "upsert",
  // Optional display-only diff (payload.changes) for the Push List inspect modal — e.g.
  // a primary change stamps "Primary contact: No → Yes". See enqueueContactPushForUser.
  payload?: Record<string, unknown> | null
): Promise<void> {
  if (!membershipId) return;
  try {
    const m = await getMembershipForContactDelete(membershipId);
    if (!m) return;
    await enqueueUnleashedPush({
      entityType: "contact",
      entityId: membershipId,
      op,
      shelterCode: m.shelter_code,
      payload,
    });
  } catch (e) {
    logger.warn("enqueueContactPushForMembership failed:", e);
  }
}

// List queue jobs (newest first) for the DFV Services Push List admin view. Small
// table (one row per pending change), so a generous cap is fine. null on error / no
// client.
export async function listQueueJobs(limit = 500): Promise<QueueRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("unleashed_push_queue")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    logger.warn("listQueueJobs failed:", error.message);
    return null;
  }
  return (data as unknown as QueueRow[]) ?? [];
}

// db-189 — CANCEL ONE queued change (the no-delete convention). Sets status='cancelled'
// with a reason instead of hard-deleting the row, so the record of the intended sync
// survives. Replaces deleteQueueJob (whose DELETE grant + "upq admin delete" policy db-189
// revokes). The worker drains by status, so a cancelled job never runs. Admin-gated by the
// existing "upq internal update" RLS policy.
export async function cancelQueueJob(
  id: number,
  reason = "Cancelled by admin"
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("unleashed_push_queue")
    .update({ status: "cancelled", last_error: reason })
    .eq("id", id);
  if (error) logger.warn("cancelQueueJob failed:", error.message);
  return { error };
}

// Count jobs that permanently errored (status='error') — powers the supervisor+
// failed-sync banner. Clears automatically once the errored jobs are retried or
// deleted. 0 on error / no client.
export async function countErroredQueueJobs(): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  const { count, error } = await c
    .from("unleashed_push_queue")
    .select("id", { count: "exact", head: true })
    .eq("status", "error");
  if (error) {
    logger.warn("countErroredQueueJobs failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// Re-queue ONE job (the Push List "Retry" on an errored row): reset it to 'queued'
// and clear the attempts/error so the next worker run attempts it fresh.
export async function requeueQueueJob(id: number): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("unleashed_push_queue")
    .update({ status: "queued", attempts: 0, last_error: null })
    .eq("id", id);
  if (error) logger.warn("requeueQueueJob failed:", error.message);
  return { error };
}
